import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Slider
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { calcularConversionesDolarAproximadas, formatearDolares, cotizacionesDolar } from './utils/cotizacionesDolar';

// Configuración de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const GraficoFacturadoProveedor = ({ movimientos, proveedorNombre }) => {
  console.log('🎯 GraficoFacturadoProveedor - Props recibidos:', { 
    movimientos: movimientos?.length, 
    proveedorNombre,
    movimientosSample: movimientos?.slice(0, 2)
  });

  const [datosProcesados, setDatosProcesados] = useState([]);
  const [rangoSlider, setRangoSlider] = useState([0, 100]); // Rango del slider como porcentaje

  const mesesDisponibles = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                           'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

  // Función auxiliar para obtener la cotización blue de un período
  const obtenerCotizacionBlue = (periodo) => {
    try {
      let año, mes;
      
      if (periodo && periodo.includes(' ')) {
        const partes = periodo.split(' ');
        mes = partes[0];
        año = partes[1];
      } else {
        return 0;
      }
      
      const mesesMap = {
        'Enero': '01', 'Febrero': '02', 'Marzo': '03', 'Abril': '04',
        'Mayo': '05', 'Junio': '06', 'Julio': '07', 'Agosto': '08',
        'Septiembre': '09', 'Octubre': '10', 'Noviembre': '11', 'Diciembre': '12'
      };
      
      const numeroMes = mesesMap[mes];
      const claveFecha = `${año}-${numeroMes}`;
      const cotizaciones = cotizacionesDolar[claveFecha];
      
      return cotizaciones?.Blue?.venta || 0;
    } catch (error) {
      return 0;
    }
  };

  // Función auxiliar para formatear variación porcentual
  const formatearVariacion = (variacion) => {
    if (variacion === null || variacion === undefined) {
      return 'N/A';
    }
    const signo = variacion >= 0 ? '+' : '';
    return `${signo}${variacion.toFixed(2)}%`;
  };

  useEffect(() => {
    if (movimientos && movimientos.length > 0) {
      procesarDatosFacturado();
    }
  }, [movimientos]);

  const procesarDatosFacturado = () => {
    console.log('📊 Procesando datos de facturación...', {
      movimientosLength: movimientos?.length,
      movimientosSample: movimientos?.slice(0, 3)
    });
    
    if (!movimientos || movimientos.length === 0) {
      console.log('⚠️ No hay movimientos para procesar');
      return;
    }
    
    // Agrupar movimientos por período completo (mes + año) y sumar facturado y abonado
    const datosPorPeriodo = new Map();
    
    movimientos.forEach((mov, index) => {
      const mes = mov.periodo;
      const año = mov.año;
      const periodoCompleto = `${mes} ${año}`;
      const facturado = parseFloat(mov.facturado) || 0;
      const abonado = parseFloat(mov.abonado) || 0;
      
      if (index < 3) {
        console.log(`📈 Procesando movimiento ${index + 1}:`, { 
          mes,
          año,
          periodoCompleto,
          facturado: mov.facturado,
          abonado: mov.abonado,
          facturadoParseado: facturado,
          abonadoParseado: abonado,
          movimientoCompleto: mov 
        });
      }
      
      if (datosPorPeriodo.has(periodoCompleto)) {
        const existing = datosPorPeriodo.get(periodoCompleto);
        datosPorPeriodo.set(periodoCompleto, {
          ...existing,
          facturado: existing.facturado + facturado,
          abonado: existing.abonado + abonado
        });
      } else {
        datosPorPeriodo.set(periodoCompleto, {
          mes,
          año: parseInt(año),
          facturado,
          abonado
        });
      }
    });

    console.log('💰 Datos por período:', Object.fromEntries(datosPorPeriodo));

    // Convertir a array y agregar conversiones USD
    const datosConConversiones = Array.from(datosPorPeriodo.entries()).map(([periodoCompleto, datos]) => {
      // Calcular conversiones USD para facturado y abonado
      const conversionesFacturado = calcularConversionesDolarAproximadas(datos.facturado, periodoCompleto);
      const conversionesAbonado = calcularConversionesDolarAproximadas(datos.abonado, periodoCompleto);
      
      // Obtener la cotización blue para el período
      const cotizacionBlue = obtenerCotizacionBlue(periodoCompleto);
      
      console.log('💱 Calculando conversión USD:', {
        periodo: periodoCompleto,
        mes: datos.mes,
        año: datos.año,
        facturadoARS: datos.facturado,
        abonadoARS: datos.abonado,
        conversionesFacturado,
        conversionesAbonado,
        cotizacionBlue
      });
      
      return {
        periodo: periodoCompleto,
        mes: datos.mes,
        año: datos.año,
        facturado_ars: datos.facturado,
        abonado_ars: datos.abonado,
        facturado_usd_oficial: conversionesFacturado.oficial,
        facturado_usd_blue: conversionesFacturado.blue,
        abonado_usd_oficial: conversionesAbonado.oficial,
        abonado_usd_blue: conversionesAbonado.blue,
        cotizacion_blue: cotizacionBlue
      };
    });

    // Ordenar por año y mes
    datosConConversiones.sort((a, b) => {
      if (a.año !== b.año) {
        return a.año - b.año;
      }
      return mesesDisponibles.indexOf(a.mes) - mesesDisponibles.indexOf(b.mes);
    });

    // Calcular variaciones porcentuales con respecto al período anterior
    const datosConVariaciones = datosConConversiones.map((dato, index) => {
      let variacion_facturado_ars = null;
      let variacion_abonado_ars = null;
      let variacion_facturado_usd = null;
      let variacion_abonado_usd = null;

      if (index > 0) {
        const periodoAnterior = datosConConversiones[index - 1];
        
        // Calcular variación Facturado ARS
        if (periodoAnterior.facturado_ars !== 0) {
          variacion_facturado_ars = ((dato.facturado_ars - periodoAnterior.facturado_ars) / periodoAnterior.facturado_ars) * 100;
        }
        
        // Calcular variación Abonado ARS
        if (periodoAnterior.abonado_ars !== 0) {
          variacion_abonado_ars = ((dato.abonado_ars - periodoAnterior.abonado_ars) / periodoAnterior.abonado_ars) * 100;
        }
        
        // Calcular variación Facturado USD
        if (periodoAnterior.facturado_usd_blue !== 0) {
          variacion_facturado_usd = ((dato.facturado_usd_blue - periodoAnterior.facturado_usd_blue) / periodoAnterior.facturado_usd_blue) * 100;
        }
        
        // Calcular variación Abonado USD
        if (periodoAnterior.abonado_usd_blue !== 0) {
          variacion_abonado_usd = ((dato.abonado_usd_blue - periodoAnterior.abonado_usd_blue) / periodoAnterior.abonado_usd_blue) * 100;
        }
      }

      return {
        ...dato,
        variacion_facturado_ars,
        variacion_abonado_ars,
        variacion_facturado_usd,
        variacion_abonado_usd
      };
    });

    console.log('✅ Datos procesados finales con variaciones:', {
      cantidad: datosConVariaciones.length,
      primeros3: datosConVariaciones.slice(0, 3),
      añosEncontrados: [...new Set(datosConVariaciones.map(d => d.año))].sort()
    });

    setDatosProcesados(datosConVariaciones);
  };

  // Nueva función de filtrado basada en slider
  const aplicarFiltrosSlider = useCallback((datos) => {
    console.log('🎚️ Aplicando filtros de slider:', {
      datosLength: datos.length,
      rangoSlider,
      porcentajeInicio: rangoSlider[0],
      porcentajeFin: rangoSlider[1]
    });
    
    if (!datos || datos.length === 0) return [];
    
    // Calcular índices basados en el porcentaje del slider
    const totalDatos = datos.length;
    const indiceInicio = Math.floor((rangoSlider[0] / 100) * (totalDatos - 1));
    const indiceFin = Math.floor((rangoSlider[1] / 100) * (totalDatos - 1));
    
    const datosFiltrados = datos.slice(indiceInicio, indiceFin + 1);
    
    console.log('✅ Datos después de filtrar con slider:', {
      totalDatos,
      indiceInicio,
      indiceFin,
      datosFiltrados: datosFiltrados.length,
      rango: datosFiltrados.length > 0 ? {
        desde: `${datosFiltrados[0].mes} ${datosFiltrados[0].año}`,
        hasta: `${datosFiltrados[datosFiltrados.length - 1].mes} ${datosFiltrados[datosFiltrados.length - 1].año}`
      } : 'Sin datos'
    });
    
    return datosFiltrados;
  }, [rangoSlider]);

  const prepararDatosGraficoLineas = useCallback(() => {
    const datosFiltrados = aplicarFiltrosSlider(datosProcesados);
    
    if (datosFiltrados.length === 0) return null;

    const labels = datosFiltrados.map(item => `${item.mes} ${item.año}`);
    const facturadoArs = datosFiltrados.map(item => item.facturado_ars);
    const abonadoArs = datosFiltrados.map(item => item.abonado_ars);
    // Calcular facturado en USD Blue: facturado_ars / cotizacion_blue
    const facturadoUsdBlue = datosFiltrados.map(item => {
      if (item.cotizacion_blue && item.cotizacion_blue > 0) {
        return item.facturado_ars / item.cotizacion_blue;
      }
      return 0;
    });

    console.log('📊 Datos preparados para gráfico de líneas (ARS vs USD):', {
      labels: labels.slice(0, 3),
      facturadoArs: facturadoArs.slice(0, 3),
      abonadoArs: abonadoArs.slice(0, 3),
      facturadoUsdBlueCalculado: facturadoUsdBlue.slice(0, 3),
      facturadoTotal: facturadoArs.reduce((a, b) => a + b, 0),
      abonadoTotal: abonadoArs.reduce((a, b) => a + b, 0)
    });

    return {
      labels,
      datasets: [
        {
          label: 'Facturado (ARS)',
          data: facturadoArs,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.1)',
          borderWidth: 2,
          tension: 0.4,
          yAxisID: 'y'
        },
        {
          label: 'Abonado (ARS)',
          data: abonadoArs,
          borderColor: 'rgb(54, 162, 235)',
          backgroundColor: 'rgba(54, 162, 235, 0.1)',
          borderWidth: 2,
          tension: 0.4,
          yAxisID: 'y'
        },
        {
          label: 'Facturado (USD Blue)',
          data: facturadoUsdBlue,
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.1)',
          borderWidth: 2,
          tension: 0.4,
          yAxisID: 'y1'
        }
      ]
    };
  }, [datosProcesados, rangoSlider, aplicarFiltrosSlider]);

  const chartOptions = useCallback(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        // Configuraciones para móviles
        bodyFont: {
          size: 14, // Tamaño más grande para móviles
          weight: 'normal'
        },
        titleFont: {
          size: 16,
          weight: 'bold'
        },
        padding: 12, // Más padding para facilitar la lectura
        cornerRadius: 8,
        displayColors: false, // Ocultar los cuadraditos de colores para ahorrar espacio
        backgroundColor: 'rgba(0, 0, 0, 0.9)', // Fondo más opaco
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.3)',
        borderWidth: 1,
        // Posicionamiento mejorado para móviles
        position: 'nearest',
        // Ancho máximo para evitar que se salga de pantalla
        maxWidth: 300,
        callbacks: {
          // Personalizar completamente el contenido del tooltip
          title: function(context) {
            return context[0].label; // Período (ej: "Enero 2024")
          },
          label: function(context) {
            return null; // No mostrar etiquetas individuales de datasets
          },
          afterLabel: function(context) {
            return null; // No usar afterLabel
          },
          beforeBody: function(context) {
            // Obtener los datos filtrados para mostrar toda la información
            const datosFiltrados = aplicarFiltrosSlider(datosProcesados);
            const puntoActual = datosFiltrados[context[0].dataIndex];
            
            if (!puntoActual) return [];
            
            let lineas = [];
            
            // Facturado en ARS con equivalentes en USD
            lineas.push(`💰 Facturado:`);
            lineas.push(`$${(puntoActual.facturado_ars || 0).toLocaleString('es-AR', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            })}`);
            
            lineas.push(``); // Línea vacía para separación
            
            lineas.push(`💵 Equivalente USD:`);
            lineas.push(`Oficial: ${(puntoActual.facturado_usd_oficial || 0).toLocaleString('es-AR', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            })}`);
            
            // Calcular USD Blue dividiendo facturado por cotización
            const facturadoUsdBlueCalculado = puntoActual.cotizacion_blue && puntoActual.cotizacion_blue > 0 
              ? (puntoActual.facturado_ars / puntoActual.cotizacion_blue)
              : 0;
              
            lineas.push(`Blue: ${facturadoUsdBlueCalculado.toLocaleString('es-AR', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            })}`);
            
            lineas.push(``); // Línea vacía para separación
            
            // Cotización Blue (solo como referencia)
            if (puntoActual.cotizacion_blue) {
              lineas.push(`📈 Cotización: $${puntoActual.cotizacion_blue.toLocaleString('es-AR', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              })}`);
            }
            
            // Variación con mes anterior (basada en ARS)
            if (puntoActual.variacion_facturado_ars !== null) {
              lineas.push(`📊 Variación: ${formatearVariacion(puntoActual.variacion_facturado_ars)}`);
            }
            
            return lineas;
          }
        }
      }
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Pesos (ARS)'
        },
        ticks: {
          callback: function(value) {
            return `$${value.toLocaleString('es-AR', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            })}`;
          }
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Dólares (USD Blue)'
        },
        ticks: {
          callback: function(value) {
            return `USD ${value.toLocaleString('es-AR', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            })}`;
          }
        },
        grid: {
          drawOnChartArea: false
        }
      }
    }
  }), [datosProcesados, rangoSlider, aplicarFiltrosSlider, formatearVariacion]);

  // Función para obtener las etiquetas del rango actual
  const obtenerEtiquetasRango = () => {
    console.log('🏷️ obtenerEtiquetasRango - Estado:', {
      datosProcesadosLength: datosProcesados.length,
      rangoSlider,
      primeros3Datos: datosProcesados.slice(0, 3)
    });
    
    if (datosProcesados.length === 0) {
      return { desde: 'Sin datos', hasta: 'Sin datos' };
    }
    
    const totalDatos = datosProcesados.length;
    const indiceInicio = Math.floor((rangoSlider[0] / 100) * Math.max(0, totalDatos - 1));
    const indiceFin = Math.floor((rangoSlider[1] / 100) * Math.max(0, totalDatos - 1));
    
    console.log('🔢 Cálculo de índices:', {
      totalDatos,
      rangoSlider,
      indiceInicio,
      indiceFin,
      datoInicio: datosProcesados[indiceInicio],
      datoFin: datosProcesados[indiceFin]
    });
    
    const datoInicio = datosProcesados[indiceInicio];
    const datoFin = datosProcesados[indiceFin];
    
    return {
      desde: datoInicio ? `${datoInicio.mes} ${datoInicio.año}` : 'Sin datos',
      hasta: datoFin ? `${datoFin.mes} ${datoFin.año}` : 'Sin datos'
    };
  };

  // Crear marcas dinámicas para el slider basadas en los datos reales
  const crearMarcasSlider = () => {
    if (datosProcesados.length === 0) return [];
    
    const marcas = [];
    const totalDatos = datosProcesados.length;
    
    // Marcar inicio, cuartos y final
    const posiciones = [0, 25, 50, 75, 100];
    
    posiciones.forEach(porcentaje => {
      const indice = Math.floor((porcentaje / 100) * Math.max(0, totalDatos - 1));
      const dato = datosProcesados[indice];
      
      if (dato) {
        marcas.push({
          value: porcentaje,
          label: porcentaje === 0 ? `${dato.mes} ${dato.año}` :
                 porcentaje === 100 ? `${dato.mes} ${dato.año}` :
                 `${porcentaje}%`
        });
      }
    });
    
    return marcas;
  };

  const marcasSlider = crearMarcasSlider();
  const etiquetasRango = obtenerEtiquetasRango();

  console.log('📊 Estado del componente:', {
    movimientosLength: movimientos?.length,
    datosProcesadosLength: datosProcesados.length,
    rangoSlider,
    etiquetasRango
  });

  // Los hooks deben ir ANTES de cualquier return condicional
  const datosGraficoLineas = useMemo(() => {
    console.log('🔍 useMemo datosGraficoLineas ejecutándose:', {
      datosProcesadosLength: datosProcesados.length,
      rangoSlider
    });
    
    if (datosProcesados.length === 0) {
      console.log('⚠️ No hay datos procesados para gráfico de líneas');
      return null;
    }
    
    const resultado = prepararDatosGraficoLineas();
    console.log('📊 Datos preparados para gráfico de líneas:', resultado);
    return resultado;
  }, [datosProcesados, rangoSlider]);

  if (!movimientos || movimientos.length === 0) {
    return (
      <Box sx={{ mt: 3, p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          📊 No hay movimientos disponibles para mostrar gráficos
        </Typography>
      </Box>
    );
  }

  if (datosProcesados.length === 0) {
    return (
      <Box sx={{ mt: 3, p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          ⏳ Procesando datos de gráficos...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h5" gutterBottom>
        💰 Facturado por Período - {proveedorNombre}
      </Typography>

      {/* Filtro de rango temporal con slider */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            📅 Seleccionar período de facturación
          </Typography>
          
          {/* Información del rango actual */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Rango seleccionado:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              Desde: <span style={{color: '#1976d2'}}>{etiquetasRango.desde}</span> • 
              Hasta: <span style={{color: '#1976d2'}}>{etiquetasRango.hasta}</span>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total de períodos: {datosProcesados.length}
            </Typography>
          </Box>

          {/* Slider de rango */}
          <Box sx={{ px: 2 }}>
            <Typography variant="body2" gutterBottom>
              Ajustar rango temporal:
            </Typography>
            <Slider
              value={rangoSlider}
              onChange={(event, newValue) => setRangoSlider(newValue)}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => {
                // Convertir porcentaje a fecha real
                const totalDatos = datosProcesados.length;
                if (totalDatos === 0) return `${value}%`;
                
                const indice = Math.floor((value / 100) * Math.max(0, totalDatos - 1));
                const dato = datosProcesados[indice];
                return dato ? `${dato.mes} ${dato.año}` : `${value}%`;
              }}
              min={0}
              max={100}
              step={Math.max(1, Math.floor(100 / Math.max(1, datosProcesados.length)))}
              marks={marcasSlider}
              sx={{ mt: 2, mb: 2 }}
            />
          </Box>

          {/* Botones de acción */}
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={() => setRangoSlider([0, 100])}
            >
              📊 Ver todo el período
            </Button>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={() => setRangoSlider([75, 100])}
            >
              📈 Últimos meses
            </Button>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={() => setRangoSlider([0, 25])}
            >
              📉 Primeros meses
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Gráfico de líneas */}
      {datosGraficoLineas && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Evolución del Facturado (ARS vs USD)
            </Typography>
            <Box sx={{ 
              height: { xs: 300, sm: 350, md: 400 }, // Altura responsive: 300px en móviles, 400px en desktop
              width: '100%',
              position: 'relative'
            }}>
              <Line data={datosGraficoLineas} options={chartOptions()} />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Mensaje si no hay datos después de filtros */}
      {!datosGraficoLineas && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              📊 No hay datos para mostrar con los filtros aplicados
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Intenta ajustar los filtros de período o limpiarlos para ver todos los datos
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default GraficoFacturadoProveedor;